import {
  NonFilterParamKeys,
  NonFilterParams,
  ParsedUrlSegmentValue,
  SearchFilterKeys,
  SearchFilters,
  SearchFiltersStateFilters,
  ValidUrlSegmentSearchFilterKeys,
  DEFAULT_PAGE_SIZE,
  nonFilterParamKeys,
  searchFilterKeys,
  searchParamKeys,
  validUrlSegmentFilterKeys,
} from '@cinch-nx/data-search'
import {
  convertFilterValueToUrlSegment,
  getDefaultSortingCriteria,
  getOrderedParsedFilterMap,
  truncateSegmentsToQueryParams,
} from '../../utils'

function addNonFilterParamToUrlParams<Key extends NonFilterParamKeys>(
  key: Key,
  value: NonFilterParams[Key],
  urlSearchParams: URLSearchParams,
  shouldUseDataDrivenSortOrderAsDefault?: boolean,
) {
  const defaultSortCriteria = getDefaultSortingCriteria(
    shouldUseDataDrivenSortOrderAsDefault,
  )
  switch (key) {
    case 'pageNumber': {
      if (value !== 1) {
        urlSearchParams.set('pageNumber', `${value}`)
      }
      break
    }
    case 'pageSize': {
      if (value !== DEFAULT_PAGE_SIZE) {
        urlSearchParams.set('pageSize', `${value}`)
      }
      break
    }
    case 'sortingCriteria': {
      if (value !== defaultSortCriteria) {
        urlSearchParams.set('sortingCriteria', `${value}`)
      }
      break
    }
    case 'useMonthly': {
      if (value) {
        urlSearchParams.set('useMonthly', `${value}`)
      }
      break
    }
  }
}
function isNonFilterParamKey(key: string): key is NonFilterParamKeys {
  return nonFilterParamKeys.includes(key as NonFilterParamKeys)
}

function isValidSegmentFilterKey(
  key: string,
): key is ValidUrlSegmentSearchFilterKeys {
  return validUrlSegmentFilterKeys.includes(
    key as ValidUrlSegmentSearchFilterKeys,
  )
}

function isStringArray(value: number | boolean | string[]): value is string[] {
  return Array.isArray(value)
}

export function mapFiltersToUrl(
  filters: Partial<SearchFiltersStateFilters>,
  allCinchStores: string[],
  existingParams: URLSearchParams = new URLSearchParams(window.location.search),
  shouldUseDataDrivenSortOrderAsDefault?: boolean,
): string {
  // remove un-set filters/params & convert to map of filter key/values & urlSearchParams
  const { urlPathMap, urlSearchParams } = Object.entries(filters).reduce(
    ({ urlPathMap, urlSearchParams }, [key, value]) => {
      if (isNonFilterParamKey(key)) {
        // special handling of non-filter params, don't include them in the url when the value is the default
        addNonFilterParamToUrlParams(
          key,
          value as NonFilterParams[NonFilterParamKeys],
          urlSearchParams,
          shouldUseDataDrivenSortOrderAsDefault,
        )
        return { urlPathMap, urlSearchParams }
      }

      if (!isStringArray(value)) {
        console.warn(
          `Unexpected type value for key '${key}', expected string[] but got ${typeof value}`,
        )

        return { urlPathMap, urlSearchParams }
      }

      // Special handling for year filters
      if ((key === 'fromYear' || key === 'toYear') && value.length === 1) {
        const otherYearKey = key === 'fromYear' ? 'toYear' : 'fromYear'
        const otherYearValue =
          filters[otherYearKey as keyof SearchFiltersStateFilters]

        // If years are different or other year is not set, use query params for years
        if (
          otherYearValue === undefined ||
          !isStringArray(otherYearValue) ||
          otherYearValue.length !== 1 ||
          otherYearValue[0] !== value[0]
        ) {
          urlSearchParams.set(key, value.join(','))
          return { urlPathMap, urlSearchParams }
        }

        // If we reach here, the years are the same
        // Only process this for fromYear to avoid duplication
        if (key === 'fromYear') {
          urlPathMap.set('vehicleYear', {
            searchApiValue: `${value[0]}-year`,
            urlSegmentValue: value[0],
          })
        }
        // Skip adding to query params since we're adding as a segment
        return { urlPathMap, urlSearchParams }
      }

      // when all possible stores are selected, show 'all-stores' in the URL params rather than listing them all
      if (
        key === 'store' &&
        allCinchStores.length > 0 &&
        value.length === allCinchStores.length &&
        allCinchStores.every((storeValue) => value.includes(storeValue))
      ) {
        urlSearchParams.set(key, 'all-stores')
      } else if (value.length === 1 && isValidSegmentFilterKey(key)) {
        // single value filters that are allowed in the URL segments should be added to the urlPathMap
        urlPathMap.set(key, {
          searchApiValue: convertFilterValueToUrlSegment(key, value[0]),
          urlSegmentValue: value[0],
        })
      } else if (
        value.length > 1 ||
        (value.length === 1 && !isValidSegmentFilterKey(key))
      ) {
        // multi-value filters and those not allowed in URL segments are added to the search params
        urlSearchParams.set(key, value.join(','))
      }
      return { urlPathMap, urlSearchParams }
    },
    {
      urlPathMap: new Map<string, ParsedUrlSegmentValue>(),
      urlSearchParams: new URLSearchParams(),
    },
  )

  // sort segments
  const { orderedParsedFilterMap: sortedParsedPath } =
    getOrderedParsedFilterMap(urlPathMap)

  // Check if we need to handle vehicleYear special case
  // If vehicleYear is present and we have more than 3 segments, we want to convert it to query params
  if (sortedParsedPath.has('vehicleYear') && sortedParsedPath.size > 3) {
    // Extract the vehicleYear value
    const vehicleYearValue =
      sortedParsedPath.get('vehicleYear')?.urlSegmentValue
    // Add fromYear and toYear as query parameters with the same value
    if (vehicleYearValue) {
      urlSearchParams.set('fromYear', vehicleYearValue)
      urlSearchParams.set('toYear', vehicleYearValue)
      // Remove vehicleYear from sortedParsedPath since we're converting it to query params
      sortedParsedPath.delete('vehicleYear')
    }
  }

  // truncate segments to 3 & convert extras to query parameters
  const {
    truncatedOrderedParsedPath: trimmedSortedParsedPath,
    additionalQueryParams,
  } = truncateSegmentsToQueryParams({
    orderedParsedFilterMap: sortedParsedPath,
    maxAllowedSegments: 3,
  })

  // append additionalQueryParams to urlSearchParams
  additionalQueryParams.forEach((_, key) => {
    if (searchFilterKeys.includes(key as SearchFilterKeys)) {
      // get the value from the original filters rather than the search params, as we don't want the
      // transformed for url segment value in search params, we want the raw one
      const searchParamValue = filters[key as keyof SearchFilters]
      if (searchParamValue) {
        urlSearchParams.set(key, searchParamValue.join(','))
      }
    }
  })

  // Add any existing non-filter URL search params
  existingParams.forEach((value, key) => {
    if (!searchParamKeys.includes(key as SearchFilterKeys)) {
      urlSearchParams.set(key, value)
    }
  })

  // sort the URL search params to maintain a stable order
  urlSearchParams.sort()

  // combine trimmedSortedParsedPath into a URL
  const urlPathSegments = [...trimmedSortedParsedPath.values()]

  // convert slashes into dashes to prevent segment overloading
  const sanitisedUrlPathSegments = urlPathSegments.map((s) =>
    s.searchApiValue.replace(/\//g, '-').replace(/\[/g, '(').replace(/]/g, ')'),
  )

  // Join the segments...
  let urlPath = sanitisedUrlPathSegments.join('/')

  // if we've got any search params, add them
  if ([...urlSearchParams.values()].length > 0) {
    urlPath += `?${urlSearchParams}`
  }

  return urlPath
}
